/**
 * The pure fixed-timestep integrator: `step(state, inputs, dt) -> state`
 * (AD-007a). Nothing here reads a clock, `Math.random`, or any module-level
 * mutable state; the only randomness comes from the seeded streams carried in
 * the state itself, and the only tier logic comes from the resolved subsystem
 * flags, never from an inline tier comparison.
 *
 * Integration is semi-implicit Euler at 60 Hz: forces are evaluated on the
 * incoming attitude, velocity advances on that acceleration, and position
 * advances on the new velocity.
 *
 * The *commanded* rotations ride on top of that and live in `kinematics.ts` —
 * AD-007h's produced turn, AD-007n's produced path curvature, AD-007j's and
 * AD-007n's two relaxations and AD-007m's precession, none of which is a torque
 * and all of which are applied after both the force update and the body-rate
 * attitude update. That file's own note says why each acts on the velocity, the
 * orientation, or both.
 */

import { wingStalled } from "./aero.ts";
import { stanceHeightM } from "./aircraft-model-frame.ts";
import { standardAtmosphere, type AirProperties } from "./atmosphere.ts";
import { advanceRates, commandedRates, controlAuthority } from "./controls.ts";
import { structuralLiftFactor, updateStructure } from "./damage.ts";
import { derelictInputs, wreckRateBias } from "./derelict.ts";
import { thrustN, updateEngine, type EngineState } from "./engine-model.ts";
import { computeForces, type ForceOutput } from "./forces.ts";
import { applyKinematics } from "./kinematics.ts";
import { FLAT_GROUND, type GroundQuery } from "./ground.ts";
import { applyGround } from "./ground-contact.ts";
import { updateGuns } from "./guns.ts";
import type { FlightInputs } from "./inputs.ts";
import { addVec3, bankAngle, lengthVec3, pitchAngle, scaleVec3, type BodyRates, type Quat, type Vec3 } from "./math3d.ts";
import { updatePilot } from "./physiology.ts";
import { RngBank } from "./rng.ts";
import { updateStallModes, type StallModeResult } from "./spin.ts";
import { collectEvents, readoutOf } from "./tick-report.ts";
import type { SimState } from "./state.ts";
import { resolveSubsystems, type SubsystemFlags } from "./tiers.ts";
import { FIXED_DT, clamp, metresToFeet } from "./units.ts";
import { windVelocity } from "./wind.ts";

interface TickContext {
  readonly sim: SimState;
  readonly flags: SubsystemFlags;
  readonly inputs: FlightInputs;
  readonly rng: RngBank;
  readonly air: AirProperties;
  readonly dt: number;
}

export interface MotionResult {
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly rates: BodyRates;
  readonly forces: ForceOutput;
  readonly stall: StallModeResult;
  /** AD-007n: the wing is out of lift — past the break, or below the 1-g stall speed. */
  readonly stalled: boolean;
  /** The wind that displaced the aircraft this tick — reported, not folded into `velocity`. */
  readonly wind: Vec3;
}

function advanceEngine(ctx: TickContext): EngineState {
  const { sim } = ctx;
  return updateEngine(sim.engine, {
    propulsion: sim.config.spec.propulsion,
    fuel: sim.config.spec.fuel,
    tunables: sim.config.tunables,
    throttleInput: ctx.inputs.throttle,
    altitudeFt: metresToFeet(sim.position.y),
    densityRatio: sim.config.altitudeLapse ? ctx.air.densityRatio : 1,
    engineHealth: sim.damage.engine,
    limitedFuel: ctx.flags.limitedFuel,
    carbIceEnabled: ctx.flags.carburettorIce,
    rng: ctx.rng,
    dt: ctx.dt,
  });
}

function forcesOn(ctx: TickContext, engine: EngineState): ForceOutput {
  const { sim } = ctx;
  const { spec, tunables } = sim.config;
  // A folded undercarriage has taken the propeller into the ground with it, so
  // the powerplant is intact and useless. Kept out of `damage` deliberately:
  // the component model is what AIRCRAFT MAY BE DAMAGED governs, and a bent
  // propeller is a ground-handling consequence, not battle damage.
  const propeller = sim.gear.collapsed ? tunables.gearCollapsedThrustFraction : 1;
  return computeForces({
    spec,
    orientation: sim.orientation,
    velocity: sim.velocity,
    air: ctx.air,
    thrustN: thrustN(spec.propulsion, engine.deliveredPowerW, lengthVec3(sim.velocity)) * propeller,
    liftFactor: structuralLiftFactor(sim.damage, tunables),
    liftAssist: ctx.flags.bankToTurnAssist,
    pitchInput: ctx.inputs.pitch,
    controlAuthority: controlAuthority(sim.damage.controls, tunables),
    tunables,
  });
}

interface TickRates {
  /** The pilot's three commanded body rates, lagged — what `sim.rates` reports. */
  readonly rates: BodyRates;
  /** What the *attitude* integrates: the same, with the elevator's share removed (AD-007n). */
  readonly attitude: BodyRates;
}

/**
 * This tick's body rates, and the one place they diverge.
 *
 * **AD-007n change 3: the elevator does not rotate the airframe.** `rates` stays
 * exactly what it was — the pilot's three commanded body rates, which is what
 * `sim.rates` reports, what AD-007m's precession is proportional to and what
 * §8.1's 22.1 °/s pins — but the attitude no longer integrates the pitch axis of
 * it. What moves the nose in pitch is the flight path moving under it
 * (`forces.pathPitchRateRadPerSec`, applied rigidly in `kinematics.ts`) plus the
 * α-relaxation closing the rest: in the original a full-back pull is a path
 * command, and the attitude follows at 0–5° of α whatever the bank.
 *
 * The pitch axis is not simply zeroed, because two subsystems legitimately do
 * rotate the airframe in pitch: autorotation, which owns the aircraft outright
 * (`controlsOverridden`, where `rates` *is* the spin's own bias), and the
 * spiral/wreck biases, which ride the axis when the pilot is not. Those are
 * taken unlagged — both are slowly-varying severity ramps, so the actuator lag
 * they skip is immaterial, and it is what makes the elevator's own share
 * subtract out exactly rather than leaving a transient.
 *
 * **On the wheels the elevator is still an elevator.** Rotating for take-off,
 * holding the tail up and putting the propeller into the ground by pushing are
 * the surface levering the airframe about its undercarriage, not a flight path
 * being commanded — there is barely a flight path to command. So the pitch axis
 * reverts to the body rate whenever the aircraft is on the ground, and the
 * produced path curvature takes over once it is not.
 */
function advanceBodyRates(
  ctx: TickContext,
  engine: EngineState,
  forces: ForceOutput,
  stall: StallModeResult,
  wreckBias: BodyRates,
): TickRates {
  const { sim } = ctx;
  const { spec, tunables } = sim.config;
  const target: BodyRates = stall.controlsOverridden
    ? wreckBias
    : commandedRates({
        control: spec.control,
        torque: spec.torque,
        flags: ctx.flags,
        tunables,
        inputs: ctx.inputs,
        rates: sim.rates,
        betaRad: forces.airData.betaRad,
        airspeedMps: forces.airData.airspeedMps,
        powerFraction: clamp(engine.deliveredPowerW / Math.max(spec.propulsion.shaftPowerW, 1), 0, 1),
        controlHealth: sim.damage.controls,
        extraRates: wreckBias,
      });
  const rates = advanceRates(sim.rates, target, ctx.dt / Math.max(spec.control.rateLagSeconds, 1e-3));
  return {
    rates,
    attitude: {
      roll: rates.roll,
      pitch: stall.controlsOverridden || sim.gear.onGround ? rates.pitch : wreckBias.pitch,
      yaw: rates.yaw,
    },
  };
}

function advanceMotion(ctx: TickContext, engine: EngineState): MotionResult {
  const { sim } = ctx;
  const { spec, tunables } = sim.config;
  const forces = forcesOn(ctx, engine);

  // AD-007n: the wing runs out of lift two ways, and with the elevator's α now
  // bounded the *speed* way is the one that keeps a stall reachable at all —
  // see `aero.ts`'s `wingStalled`. Airborne only: a parked aeroplane is slow,
  // not stalled.
  const stalled =
    !sim.gear.onGround &&
    wingStalled(spec.aero, forces.airData.alphaRad, forces.airData.airspeedMps, spec.massKg, ctx.air.densityKgM3);

  const stall = updateStallModes(sim.stallModes, {
    stall: spec.stall,
    flags: ctx.flags,
    tunables,
    inputs: ctx.inputs,
    stalled,
    alphaRad: forces.airData.alphaRad,
    betaRad: forces.airData.betaRad,
    airspeedMps: forces.airData.airspeedMps,
    alphaCollapseBandRad: spec.aero.alphaStallRad + spec.aero.postStallSpreadRad,
    bankRad: bankAngle(sim.orientation),
    loadFactor: forces.loadFactor,
    rates: sim.rates,
    dt: ctx.dt,
  });

  const wreckBias = wreckRateBias({
    damage: sim.damage,
    pilot: sim.pilot,
    tunables,
    alphaRad: forces.airData.alphaRad,
    stallBias: stall.rateBias,
  });
  const { rates, attitude } = advanceBodyRates(ctx, engine, forces, stall, wreckBias);
  const kinematic = applyKinematics({
    sim,
    flags: ctx.flags,
    forces,
    rates: attitude,
    commandedPitchRate: rates.pitch,
    dt: ctx.dt,
  });
  // AD-007k: wind is a world-frame velocity added to the *flight path* only. It
  // goes into the position integration and deliberately not into `velocity`,
  // exactly as the original does it — the original sums the band's vector with
  // the object's velocity inside a local and never writes the sum back. So the
  // aeroplane drifts over the ground while its airspeed, angle of attack, trim
  // and stall margin are all untouched, which is both what `RB.EXE` does and
  // what moving the whole air mass physically means.
  const wind = windVelocity({
    altitudeM: sim.position.y,
    // Last tick's contact state: `applyGround` has not run yet this tick, and
    // the original likewise tests a ground flag settled earlier in the update.
    grounded: sim.gear.onGround,
    enabled: ctx.flags.wind,
    weather: sim.config.weather,
    tunables,
  });
  return {
    position: addVec3(sim.position, scaleVec3(addVec3(kinematic.velocity, wind), ctx.dt)),
    velocity: kinematic.velocity,
    orientation: kinematic.orientation,
    rates,
    forces,
    stall,
    stalled,
    wind,
  };
}

function restingTick(sim: SimState, dt: number): SimState {
  return { ...sim, tick: sim.tick + 1, timeS: sim.timeS + dt, events: [] };
}

/**
 * Advances the sim one fixed tick. Pure: the returned state shares nothing
 * mutable with `sim`, and `ground` is consulted only as a pure function of
 * position, so a state hash stays a function of the flight alone (AD-007a).
 * The default is flat datum — `ground.ts` for the seam and who fills it in.
 */
export function step(
  sim: SimState,
  inputs: FlightInputs,
  dt: number = FIXED_DT,
  ground: GroundQuery = FLAT_GROUND,
): SimState {
  if (sim.crashed) return restingTick(sim, dt);

  const { config } = sim;
  const rng = new RngBank(sim.rng);
  const flags = resolveSubsystems(config.tier, config.realism);
  const ctx: TickContext = {
    sim,
    flags,
    inputs: derelictInputs(inputs, sim.pilot, sim.damage, bankAngle(sim.orientation), config.tunables),
    rng,
    air: standardAtmosphere(config.altitudeLapse ? sim.position.y : 0),
    dt,
  };

  const engine = advanceEngine(ctx);
  const motion = advanceMotion(ctx, engine);
  const grounding = applyGround({
    sim,
    inputs: ctx.inputs,
    motion: { ...motion, liftN: motion.forces.liftN },
    ground,
    dt,
  });

  const structure = updateStructure(sim.damage, {
    structure: config.spec.structure,
    flags,
    tunables: config.tunables,
    loadFactor: motion.forces.loadFactor,
    airspeedMps: motion.forces.airData.airspeedMps,
    vmaxMps: config.spec.vmaxMps,
    rng,
    dt,
  });

  const guns = updateGuns(sim.guns, {
    gun: config.spec.guns,
    tunables: config.tunables,
    jamsEnabled: flags.gunJams,
    // AD-011 §9.7: the belt picks which of `.DAT` 210/212/214 is in force.
    ammunition: sim.config.ammunition,
    limitedAmmunition: flags.limitedAmmunition,
    firing: ctx.inputs.firing,
    clearRequested: ctx.inputs.clearJam,
    rng,
    dt,
  });

  const next: SimState = {
    config,
    tick: sim.tick + 1,
    timeS: sim.timeS + dt,
    position: grounding.position,
    velocity: grounding.velocity,
    orientation: grounding.orientation,
    rates: grounding.rates,
    engine,
    guns: guns.guns,
    pilot: updatePilot(sim.pilot, {
      hypoxiaEnabled: flags.hypoxia,
      altitudeFt: metresToFeet(grounding.position.y),
      tunables: config.tunables,
      dt,
    }),
    damage: structure.damage,
    stallModes: motion.stall.state,
    gear: grounding.gear,
    readout: readoutOf(
      motion,
      grounding,
      // The stance at the attitude this tick ended in, not the parked one —
      // `ground-contact.ts` places the wheels the same way, and an AGL that
      // read a different stance would not be zero on the ground.
      stanceHeightM(config.spec.undercarriage.groundHullUnits, pitchAngle(grounding.orientation)),
      motion.stalled,
    ),
    rng: rng.snapshot(),
    events: [],
    crashed: grounding.crashed,
  };

  return {
    ...next,
    events: collectEvents(sim, next, { wingFailedNow: structure.wingFailedNow, guns, grounding }),
  };
}
